import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { useLanguage } from "@/i18n";
import { CountrySwitcher } from "@/components/CountrySwitcher";

const navLinks = [
  { label: "Devis IA en 5 min", href: "/#assistant", hash: "#assistant", testId: "nav-link-assistant" },
  { label: "Bâtiments", href: "/#batiments", hash: "#batiments", testId: "nav-link-buildings", secondary: true },
  { label: "Contrats", href: "/#contrats", hash: "#contrats", testId: "nav-link-contracts", secondary: true },
  { label: "Automatisations", href: "/#comment", hash: "#comment", testId: "nav-link-how" },
  { label: "Facturation", href: "/#facturation", hash: "#facturation", testId: "nav-link-invoicing" },
  { label: "Comptable", href: "/#comptable", hash: "#comptable", testId: "nav-link-accountant", secondary: true },
  { label: "Fonctionnalités", href: "/#fonctionnalites", hash: "#fonctionnalites", testId: "nav-link-features" },
  { label: "Tarifs", href: "/tarifs", hash: "", testId: "nav-link-pricing" },
  { label: "Contact", href: "/#contact", hash: "#contact", testId: "nav-link-contact", secondary: true },
];

const LangSwitcher = ({ mobile = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"}
      className="inline-flex items-center gap-1 rounded-full border border-[#E2E7F7] bg-white p-1"
      role="group"
      aria-label="Langue / Taal"
    >
      <Globe className="w-3.5 h-3.5 text-[#93A0BC] ml-1.5" />
      {["fr", "nl", "es"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-${l}${mobile ? "-mobile" : ""}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] ${
            lang === l ? "bg-[#3A5CC8] text-white" : "text-[#5C6A87] hover:text-[#0F2645]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const [account, setAccount] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const role = localStorage.getItem("devisto_role");
    const token = localStorage.getItem("devisto_token");
    if (role && token && (role === "artisan" || role === "pro" || role === "entreprise")) {
      setAccount("/studio");
    }
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, hash]);

  const isActive = (link) => {
    if (link.href === "/a-propos") return pathname === "/a-propos";
    if (link.href === "/tarifs") return pathname === "/tarifs";
    if (link.href === "/blog") return pathname.startsWith("/blog");
    if (link.href === "/") return pathname === "/" && !hash;
    return pathname === "/" && hash === link.hash;
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/90 border-b border-[#E2E7F7]"
    >
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between gap-2 px-4 sm:px-6 lg:px-8">
        <a
          href="/"
          data-testid="header-logo"
          className="flex items-center gap-2 shrink-0 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] rounded-md"
        >
          <DevistoLogo className="h-6 sm:h-7 w-auto" />
        </a>
        <nav className="hidden xl:flex items-center gap-0.5" aria-label={t("Navigation principale")}>
          {navLinks.map((link) => (
            <a
              key={link.testId}
              href={link.href}
              data-testid={link.testId}
              className={`relative whitespace-nowrap text-[13px] font-medium px-2.5 py-2 transition-colors duration-200 ${link.secondary ? "hidden 2xl:inline-block" : ""} ${
                isActive(link) ? "text-[#0F2645]" : "text-[#5C6A87] hover:text-[#3A5CC8]"
              }`}
            >
              {t(link.label)}
              {isActive(link) && (
                <span className="absolute -bottom-0.5 left-2.5 right-2.5 h-0.5 bg-[#3A5CC8] rounded-full" />
              )}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-2 sm:flex">
            <CountrySwitcher />
            <LangSwitcher />
          </div>
          <Link
            to={account || "/connexion"}
            data-testid="header-account-link"
            className="hidden md:inline-flex xl:hidden 2xl:inline-flex items-center whitespace-nowrap rounded-lg border-2 border-[#414E6B] px-3.5 py-2 text-[13px] font-medium text-[#0F2645] hover:bg-[#414E6B] hover:text-white transition-[background-color,color] duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            {account ? t("Mon espace") : t("Se connecter")}
          </Link>
          <Link
            to="/studio/inscription"
            data-testid="header-signup-btn"
            className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-xl bg-[#3A5CC8] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_10px_24px_-14px_rgba(58,92,200,0.9)] hover:bg-[#3A5CC8] active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            <span className="hidden sm:inline">{t("Essai 30 jours gratuit")}</span>
            <span className="sm:hidden">{t("Essai gratuit")}</span>
          </Link>
          <button
            type="button"
            data-testid="header-menu-btn"
            aria-label={menuOpen ? t("Fermer le menu") : t("Ouvrir le menu")}
            onClick={() => setMenuOpen(!menuOpen)}
            className="2xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-[#E2E7F7] text-[#0F2645] hover:bg-[#F6F8FD] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          data-testid="header-mobile-menu"
          aria-label={t("Navigation mobile")}
          className="2xl:hidden bg-white border-t border-[#E2E7F7] px-6 py-4 shadow-lg"
        >
          <div className="mb-1 flex flex-wrap items-center gap-2 border-b border-[#E2E7F7] pb-3">
            <CountrySwitcher mobile />
            <LangSwitcher mobile />
          </div>
          {navLinks.map((link) => (
            <a
              key={link.testId}
              href={link.href}
              data-testid={`mobile-${link.testId}`}
              className={`block py-3 text-base font-medium border-b border-[#E2E7F7] last:border-0 transition-colors duration-200 ${
                isActive(link) ? "text-[#3A5CC8]" : "text-[#5C6A87] hover:text-[#0F2645]"
              }`}
            >
              {t(link.label)}
            </a>
          ))}
          <Link
            to={account || "/connexion"}
            data-testid="mobile-header-account-link"
            className="block py-3 text-base font-medium text-[#5C6A87] hover:text-[#0F2645] transition-colors duration-200"
          >
            {account ? t("Mon espace") : t("Se connecter")}
          </Link>
        </nav>
      )}
    </header>
  );
};
